import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Users, Calendar, MessageSquare } from 'lucide-react';
import { childConnectionService } from '@/services/childConnectionService';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface ParentChildConnectorProps {
  parentId: string;
  userRole: string;
  onConnectionSuccess?: () => void;
}

const ParentChildConnector: React.FC<ParentChildConnectorProps> = ({ 
  parentId, 
  userRole,
  onConnectionSuccess 
}) => {
  const [connectionCode, setConnectionCode] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connectedChildren, setConnectedChildren] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Only show for parent users
  if (userRole !== 'parent') {
    return null;
  }

  useEffect(() => {
    loadConnectedChildren();
  }, [parentId]);

  const loadConnectedChildren = async () => {
    setLoading(true);
    try {
      console.log('🔄 ParentChildConnector - Loading connected children for parent:', parentId);
      console.log('🔄 ParentChildConnector - Parent ID type:', typeof parentId, 'Length:', parentId?.length);
      
      if (!parentId || parentId.trim() === '') {
        console.log('❌ ParentChildConnector - Invalid parent ID:', parentId);
        setConnectedChildren([]);
        return;
      }
      
      const children = await childConnectionService.getConnectedChildren(parentId);
      console.log('📋 ParentChildConnector - Connected children data:', children);
      console.log('📋 ParentChildConnector - Children count:', children?.length || 0);
      
      setConnectedChildren(children);
    } catch (error) {
      console.error('❌ Error loading connected children:', error);
      toast({
        title: "Loading Error",
        description: "Failed to load connected children. Please refresh the page.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!connectionCode.trim()) {
      toast({
        title: "Code Required",
        description: "Please enter your child's connection code",
        variant: "destructive"
      });
      return;
    }

    setConnecting(true);
    try {
      const result = await childConnectionService.connectParentToChild(
        parentId, 
        connectionCode.trim().toUpperCase()
      );

      console.log('🔗 Connection result:', result);
      
      if (result.success) {
        toast({
          title: "Connected Successfully!",
          description: `You are now connected to ${result.child_username}. You can see their teams and join their chat groups.`,
        });
        setConnectionCode('');
        loadConnectedChildren();
        onConnectionSuccess?.();
      } else {
        const errorMessage = result.error || "Invalid or expired connection code";
        const isExpired = (result as any).expired === true || errorMessage.toLowerCase().includes('expired');
        console.log('❌ Connection failed:', errorMessage, 'Expired:', isExpired);
        
        // Special handling for different error cases
        if (errorMessage.toLowerCase().includes('already')) {
          toast({
            title: "Already Connected!",
            description: "You are already connected to this child. Check your connected children list below.",
            variant: "default"
          });
        } else if (isExpired) {
          toast({
            title: "Code Expired",
            description: "This code has expired. A new code has been generated for your child - please ask them to check their dashboard and share the new code with you.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Connection Failed",
            description: errorMessage,
            variant: "destructive"
          });
        }
        
        // Always refresh data even if connection failed, in case the connection already exists
        loadConnectedChildren();
      }
    } catch (error) {
      console.error('Error connecting to child:', error);
      toast({
        title: "Connection Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConnect();
    }
  };

  return (
    <div className="space-y-4">
      {/* Connection Form */}
      <Card className="bg-gradient-to-br from-accent/10 to-primary/10 border-accent/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-accent flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Connect to Your Child
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Enter your child's connection code to link their account and access their teams and activities.
          </p>
          
          <div className="space-y-2">
            <Label htmlFor="connection-code">Child's Connection Code</Label>
            <div className="flex gap-2">
              <Input
                id="connection-code"
                value={connectionCode}
                onChange={(e) => setConnectionCode(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                placeholder="Enter 6-character code"
                maxLength={6}
                className="font-mono text-center tracking-widest"
                disabled={connecting}
              />
              <Button 
                onClick={handleConnect} 
                disabled={connecting || !connectionCode.trim()}
                className="whitespace-nowrap"
              >
                {connecting ? 'Connecting...' : 'Connect'}
              </Button>
            </div>
          </div>

          <div className="text-xs text-muted-foreground bg-muted/30 rounded p-3">
            <p className="font-medium mb-1">🔍 Where to find the code:</p>
            <p>Ask your child to check their dashboard - they'll see a "My Connection Code" section with a 6-character code to share with you.</p>
          </div>
        </CardContent>
      </Card>

      {/* Connected Children */}
      {!loading && connectedChildren.length > 0 && (
        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-primary flex items-center gap-2">
              <Users className="h-5 w-5" />
              Connected Children ({connectedChildren.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {connectedChildren.map((connection) => (
                <div key={connection.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Users className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{connection.child?.username}</p>
                      <p className="text-sm text-muted-foreground">
                        Connected on {new Date(connection.connected_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => navigate(`/child-teams?child=${connection.child_id}`)}
                    >
                      <Calendar className="h-4 w-4 mr-1" />
                      Teams
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => navigate(`/chat?child=${connection.child_id}`)}
                    >
                      <MessageSquare className="h-4 w-4 mr-1" />
                      Chats
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ParentChildConnector;